import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { UILoader } from '@futrli/ui-components';
import { useWorkspaceOrganisations } from '@futrli/organisations';
import { getConsolidatedForecastUrl, PREDICT_PRODUCT_SLUG, ROUTES, useLocalStorage } from '@futrli/shared-services';

import { AvailableTemplates } from '../../components/available-templates/available-templates';
import { ConsolidationInstructions } from '../../components/consolidation-instructions/consolidation-instructions';
import { ImportPageWrapper } from '../../components/import-page-wrapper/import-page-wrapper';
import { ImportTitle } from '../../components/import-title/import-title';
import { LinkedTemplateList } from '../../components/linked-templates-list/linked-template-list';
import { LOCAL_STORAGE_REDIRECT_KEY } from '../../constants';
import { sectionsAndTemplates, Template } from '../../templates';
import { SyncAllModal } from '../../components/sync-all-modal/sync-all-modal';
import { trackEvent } from '../../../../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../../../../core/constants';
import { useConsolidatedForecastContext } from '../../../../../../../forecast/detail/context/consolidated-forecast-context';
import { useConsolidationDataContext } from '../../../../../../../predict-management/context/consolidation-data-context';
import { useLinkedSheetData } from '../../../hooks/use-linked-sheet-data/use-linked-sheet-data';

import styles from './import-consolidation.scss';
import { getRedirectURL } from '../../../url-utils';

export const ImportConsolidation = () => {
  const {
    forecast,
    isLoading: isForecastDataLoading,
    refresh: refreshForecast,
    forecastId
  } = useConsolidatedForecastContext();
  const { workspaceId, consolidationId } = useConsolidationDataContext();
  const { isDataLoading: isOrgDataLoading } = useWorkspaceOrganisations(workspaceId);

  const redirectURL = getRedirectURL(workspaceId, consolidationId, forecastId!, ROUTES.IMPORT_REDIRECT);
  const { data: linkedSheets, refreshSheetsInfo } = useLinkedSheetData(redirectURL);

  const navigate = useNavigate();

  const isAtLeastOneSheetAccessible = useMemo(() => {
    return !!linkedSheets.find(sheet => !!sheet.sheetInfo?.data?.import_sheet_info?.sheet_accessible);
  }, [linkedSheets]);

  const [, setLoginRedirectUrl] = useLocalStorage(LOCAL_STORAGE_REDIRECT_KEY, '');

  const [showSyncModal, setShowSyncModal] = useState(false);

  const handleOpenTemplate = (template: Template) => {
    trackEvent(TRACKING_EVENTS.IMPORT_OPEN_TEMPLATE, { templateName: template.linkName });
    window.open(template.link, '_blank');
  };

  const handleCancelClick = useCallback(() => {
    navigate(getConsolidatedForecastUrl(workspaceId, consolidationId, PREDICT_PRODUCT_SLUG, forecastId));
  }, [consolidationId, forecastId, navigate, workspaceId]);

  const handleCloseSyncModal = useCallback(
    (refreshNeeded: boolean) => {
      setShowSyncModal(false);
      if (refreshNeeded) {
        refreshSheetsInfo();
        refreshForecast();
      }
    },
    [refreshForecast, refreshSheetsInfo]
  );

  const handleLoginClick = useCallback(() => {
    setLoginRedirectUrl(window.location.pathname);
    trackEvent(TRACKING_EVENTS.IMPORT_AUTHORISE);
  }, [setLoginRedirectUrl]);

  const handleSyncAll = useCallback(() => {
    setShowSyncModal(true);
  }, []);

  // show loading spinner if info loading
  // unless we are currently creating an import sheet in the open modal
  if (isForecastDataLoading || isOrgDataLoading) {
    return (
      <section data-testid="import-page-loading" className={styles.loading}>
        <UILoader />
      </section>
    );
  }

  return (
    <>
      <ImportPageWrapper forecastName={forecast?.name} onCancel={handleCancelClick}>
        <section className={styles.container} data-testid="import-page">
          <ImportTitle
            disableSyncAll={!isAtLeastOneSheetAccessible}
            onSyncAll={handleSyncAll}
            title={<FormattedMessage id="predict.import_templates" defaultMessage="Import templates" />}
          />
          <LinkedTemplateList data={linkedSheets} onLoginClick={handleLoginClick} style={{ marginTop: '25px' }} />
          {isAtLeastOneSheetAccessible && (
            <>
              <ConsolidationInstructions style={{ marginTop: '40px' }} />
              <AvailableTemplates
                className={styles.availableTemplates}
                data={sectionsAndTemplates}
                isSheetAccessible={isAtLeastOneSheetAccessible}
                onOpenTemplate={handleOpenTemplate}
                style={{ marginTop: '30px' }}
              />
            </>
          )}
        </section>

        {showSyncModal && <SyncAllModal data={linkedSheets} onClose={handleCloseSyncModal} />}
      </ImportPageWrapper>
    </>
  );
};
